import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, User, Tag, ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    axios.get(`${API}/blog`).then(({ data }) => setPosts(data)).catch(() => {});
  }, []);

  const categories = ["All", ...new Set(posts.map((p) => p.category))];

  const filtered = posts.filter((p) => {
    const matchCategory = activeCategory === "All" || p.category === activeCategory;
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || (p.excerpt || "").toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="container-custom text-center">
          <p className="text-red-400 text-sm font-medium uppercase tracking-wider mb-3">Travel Blog</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Travel Tips & Inspiration</h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">Discover destination guides, travel hacks, and insider tips from our experts.</p>
          <div className="max-w-md mx-auto mt-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-12 rounded-full"
              data-testid="blog-search"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container-custom py-16">
        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap mb-10 justify-center">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === c ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        {filtered.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No articles found. Check back soon!</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group" data-testid={`blog-card-${post.slug}`}>
                <article className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1">
                  {post.cover_image ? (
                    <img src={post.cover_image} alt={post.title} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                      <span className="text-white/40 text-4xl font-bold">D</span>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded">{post.category}</span>
                      {post.published_at && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(post.published_at).toLocaleDateString()}</span>
                      )}
                    </div>
                    <h3 className="font-bold text-slate-800 text-lg leading-snug mb-2 group-hover:text-red-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    {post.excerpt && <p className="text-sm text-slate-500 line-clamp-2 mb-3">{post.excerpt}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
                      <span className="text-red-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
